
export const getTopicSummaryHTML = (summaryByTopic: any[]) => {
  if (!summaryByTopic || summaryByTopic.length === 0) return "";

  const domainGroups: Record<string, any[]> = {};
  const standaloneTopics: any[] = [];

  summaryByTopic.forEach((item: any) => {
    if (!item.domainTopic) {
      standaloneTopics.push(item);
    } else {
      if (!domainGroups[item.domainTopic]) {
        domainGroups[item.domainTopic] = [];
      }
      domainGroups[item.domainTopic].push(item);
    }
  });

  const renderTopic = (topic: any, index: number, isGrouped: boolean) => {
    const alphabet = String.fromCharCode(97 + index);

    return `
      <div style="margin-left:${isGrouped ? "28px" : "12px"}; margin-bottom:6px;">
        
        <div style="font-size:14px; font-weight:600; margin-bottom:2px;">
          ${alphabet}. ${topic.topic}
        </div>

        <ul style="
          padding-left:18px;
          margin:2px 0 6px;
        ">
          ${(topic.summary || [])
            .map((s: any) => `<li style="margin-bottom:2px;">${s}</li>`)
            .join("")}
        </ul>

      </div>
    `;
  };

  let html = "";

  Object.entries(domainGroups).forEach(([domain, topics]) => {
    html += `
      <div style="margin-bottom:14px;">
        
        <div style="font-size:16px; font-weight:700; margin-bottom:4px;">
          ${domain}
        </div>

        ${topics.map((t, i) => renderTopic(t, i, true)).join("")}

      </div>
    `;
  });

  if (standaloneTopics.length > 0) {
    html += `
      <div style="margin-bottom:10px;">
        ${standaloneTopics.map((t, i) => renderTopic(t, i, false)).join("")}
      </div>
    `;
  }

  return html;
};

export const getKeyTakeawaysHTML = (keyTakeaways: any[] = []) => {
  if (!Array.isArray(keyTakeaways) || keyTakeaways.length === 0) return "";

  const title = `<h3 style="font-size:16px; font-weight:bold;">Key Takeaways</h3>\n`;
  const items = keyTakeaways
    .map((item) => {
      const header = String(item?.header ?? "").trim();
      const description = String(item?.description ?? "").trim();
      const line = header && description ? `${header} - ${description}` : header || description;
      if (!line) return "";
      // Include leading bullet + newlines so plain-text stripping still keeps readable bullets.
      return `
       <li> ${line}</li>`;
    })
    .filter(Boolean)
    .join("\n");

  if (!items) return "";
  return `${title}<ul style="padding-left:20px; margin:0;">${items}</ul>`;
};

export const getSentimentHTML = (response: any) => {
  if (!response || response.length === 0) return "";

  const analytics = response[0];
  const isAgendaPresent = analytics.isAgendaPresent ? "Yes" : "No";
  const sentiment = analytics.sentiment?.overallSentiment || "N/A";
  const sentimentSummary = analytics.sentiment?.sentimentSummary || "No summary available.";

  return `
    <div style="font-family:Arial, sans-serif; line-height:1.6;">
      <h2 style="font-size:16px; font-weight:bold; margin-bottom:4px;">Executive Summary</h2>

      <h3 style="font-size:16px; font-weight:bold; margin-bottom:4px;">Was an agenda discussed at the beginning of the interaction?</h3>
      <p style="margin:0 0 0px 20px;">${isAgendaPresent}</p>

      <h3 style="font-size:16px; font-weight:bold; margin-bottom:4px;">How did the interaction go?</h3>
      <p style="margin:0 0 0px 20px;"><strong>${sentiment}</strong></p>

      <h3 style="font-size:16px; font-weight:bold; margin-bottom:4px;">Summary</h3>
      <p style="margin:0 0 0 20px;">${sentimentSummary}</p>
    </div>
  `;
};

export const getCallQualityHTML = (callQualityData: any) => {
  if (!callQualityData) return "";

  const sections = ["opening", "execution", "closing"] as const;

  return `
    <div style="font-family:Arial, sans-serif; line-height:1.6;">
      ${sections
        .map(sectionKey => {
          const section = callQualityData[sectionKey];
          if (!section) return "";
          const sectionTitle = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
          const score = section.score !== null ? section.score : "N/A";
          const questions = section.questions?.length
            ? section.questions.map((q: string) => `<li style="margin-bottom:2px;">${q}</li>`).join("")
            : "<li style='margin-bottom:2px;'>No questions recorded.</li>";
          const wentWell = section.whatWentWell?.length
            ? section.whatWentWell.map((w: string) => `<li style="margin-bottom:2px;">${w}</li>`).join("")
            : "<li style='margin-bottom:2px;'>None recorded.</li>";
          const areasOfOpportunity = section.areasOfOpportunity?.length
            ? section.areasOfOpportunity.map((a: string) => `<li style="margin-bottom:2px;">${a}</li>`).join("")
            : "<li style='margin-bottom:2px;'>None recorded.</li>";

          return `
            <h3 style="font-size:16px; font-weight:bold; margin-bottom:4px;">${sectionTitle}:</h3>
            <p style="margin:0 0 8px 20px;"><strong>Score:</strong> ${score}</p>
            <h4 style="font-size:14px; font-weight:bold; margin-bottom:2px; margin-left:20px;">Questions:</h4>
            <ul style="padding-left:40px; margin-top:0; margin-bottom:8px;">${questions}</ul>
            <h4 style="font-size:14px; font-weight:bold; margin-bottom:2px; margin-left:20px;">What went well:</h4>
            <ul style="padding-left:40px; margin-top:0; margin-bottom:8px;">${wentWell}</ul>
            <h4 style="font-size:14px; font-weight:bold; margin-bottom:2px; margin-left:20px;">Areas of Opportunity:</h4>
            <ul style="padding-left:40px; margin-top:0; margin-bottom:10px;">${areasOfOpportunity}</ul>
            <br/>
          `;
        })
        .join("")}
    </div>
  `;
};

export const getActionItemsHTML = (actionItemsData: any[]) => {
  if (!actionItemsData || actionItemsData.length === 0) return "";

  const renderAssigneeGroups = (items: any[]) => {
    const assigneeGroups: Record<string, any[]> = {};

    items.forEach((item: any) => {
      const assignee =
        item.assigneeName?.trim() || "Unassigned";

      if (!assigneeGroups[assignee]) {
        assigneeGroups[assignee] = [];
      }

      assigneeGroups[assignee].push(item);
    });

    return Object.entries(assigneeGroups)
      .map(([assigneeName, assigneeItems]) => {
        return `
          <div style="margin-left:16px; margin-bottom:2px;">
            <h5 style="font-size:14px; font-weight:bold; margin-left:8px;">
              • ${assigneeName}
            </h5>

            ${assigneeItems
              .map((item: any) => {
                const title = item.title || "No title";

                const dueDate = item.dueDate
                  ? ` - Due Date: ${item.dueDate}`
                  : "";

                return `
                  <p style="margin:0 0 0 20px;">
                    • ${title} ${dueDate}
                  </p>
                `;
              })
              .join("")}
          </div>
        `;
      })
      .join("");
  };

  const hasAnyDomainTopic = actionItemsData.some(
    (item) => (item.domainTopic || "").trim().length > 0
  );

  if (!hasAnyDomainTopic) {
    return `
      <div style="font-family:Arial, sans-serif; line-height:1.6;">
        ${renderAssigneeGroups(actionItemsData)}
      </div>
    `;
  }

  const groups: Record<string, any[]> = {};

  actionItemsData.forEach((item) => {
    const raw = (item.domainTopic || "").trim();

    const key =
      !raw ||
      raw.toLowerCase() === "other" ||
      raw.toLowerCase() === "others"
        ? "Other"
        : raw;

    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  return `
    <div style="font-family:Arial, sans-serif; line-height:1.2;">
      ${Object.entries(groups)
        .map(([groupName, items]) => {
          return `
            <h3 style="font-size:16px; font-weight:bold; margin-top:12px;">
              ${groupName}
            </h3>

            ${renderAssigneeGroups(items)}
          `;
        })
        .join("")}
    </div>
  `;
};